import { z } from 'zod';


// #/components/schemas/tradingSearchSport
export const TradingSearchSportZod = z.object({
    sportId: z.string(),
    name: z.string(),
});
export type TradingSearchSportType = z.TypeOf<typeof TradingSearchSportZod>;


// #/components/schemas/tradingSearchPaginationInfo
export const TradingSearchPaginationInfoZod = z.object({
    page: z.number(),
    pageSize: z.number(),
    pageCount: z.number(),
});
export type TradingSearchPaginationInfoType = z.TypeOf<typeof TradingSearchPaginationInfoZod>;


// #/components/schemas/tradingSearchPagedResponseSport
export const TradingSearchPagedResponseSportZod = z.object({
    items: z.array(TradingSearchSportZod),
    pagination: TradingSearchPaginationInfoZod,
});
export type TradingSearchPagedResponseSportType = z.TypeOf<typeof TradingSearchPagedResponseSportZod>;


// #/components/schemas/tradingSearchErrorResponseInner
export const TradingSearchErrorResponseInnerZod = z.object({
    code: z.number(),
    message: z.string().optional(),
});
export type TradingSearchErrorResponseInnerType = z.TypeOf<typeof TradingSearchErrorResponseInnerZod>;


// #/components/schemas/tradingSearchErrorResponse
export const TradingSearchErrorResponseZod = z.object({
    error: TradingSearchErrorResponseInnerZod,
});
export type TradingSearchErrorResponseType = z.TypeOf<typeof TradingSearchErrorResponseZod>;


// #/components/schemas/tradingSearchCompetition
export const TradingSearchCompetitionZod = z.object({
    competitionId: z.string(),
    sportId: z.string(),
    name: z.string(),
    updatedAt:  z.union([ z.string().optional(), z.null()]),
    createdAt: z.union([ z.string().optional(), z.null()]),
    active: z.boolean(),
});
export type TradingSearchCompetitionType = z.TypeOf<typeof TradingSearchCompetitionZod>;


// #/components/schemas/tradingSearchPagedResponseCompetition
export const TradingSearchPagedResponseCompetitionZod = z.object({
    items: z.array(TradingSearchCompetitionZod),
    pagination: TradingSearchPaginationInfoZod,
});
export type TradingSearchPagedResponseCompetitionType = z.TypeOf<typeof TradingSearchPagedResponseCompetitionZod>;


// #/components/schemas/tradingSearchTimeline
export const TradingSearchTimelineZod = z.union([z.literal('prematch'), z.literal('show'), z.literal('started'), z.literal('finished')]);
export type TradingSearchTimelineType = z.TypeOf<typeof TradingSearchTimelineZod>;


// #/components/schemas/tradingSearchTimeSettings
export const TradingSearchTimeSettingsZod = z.object({
    startTime: z.string(),
    timeZone: z.string(),
    timeline: TradingSearchTimelineZod,
});
export type TradingSearchTimeSettingsType = z.TypeOf<typeof TradingSearchTimeSettingsZod>;


// #/components/schemas/tradingSearchTradingState
export const TradingSearchTradingStateZod = z.union([z.literal('new'), z.literal('open'), z.literal('resulted'), z.literal('settling'), z.literal('settled'), z.literal('suspended')]);
export type TradingSearchTradingStateType = z.TypeOf<typeof TradingSearchTradingStateZod>;


// #/components/schemas/tradingSearchEvent
export const TradingSearchEventZod = z.object({
    eventId: z.string(),
    competitionId: z.string(),
    competitionName: z.string(),
    sportId: z.string(),
    name: z.string(),
    timeSettings: TradingSearchTimeSettingsZod,
    active: z.boolean(),
    state: TradingSearchTradingStateZod,
    isOutright: z.boolean(),
    isAntePost: z.boolean(),
});
export type TradingSearchEventType = z.TypeOf<typeof TradingSearchEventZod>;


// #/components/schemas/tradingSearchPagedResponseEvent
export const TradingSearchPagedResponseEventZod = z.object({
    items: z.array(TradingSearchEventZod),
    pagination: TradingSearchPaginationInfoZod,
});
export type TradingSearchPagedResponseEventType = z.TypeOf<typeof TradingSearchPagedResponseEventZod>;


// #/components/schemas/tradingSearchPrice
export const TradingSearchPriceZod = z.object({
    decimal: z.number(),
    fractional: z.string(),
});
export type TradingSearchPriceType = z.TypeOf<typeof TradingSearchPriceZod>;


// #/components/schemas/tradingSearchSelectionResultType
export const TradingSearchSelectionResultTypeZod = z.union([z.literal('won'), z.literal('lost'), z.literal('place'), z.literal('void'), z.literal('halfwon'), z.literal('halflost')]);
export type TradingSearchSelectionResultTypeType = z.TypeOf<typeof TradingSearchSelectionResultTypeZod>;


// #/components/schemas/tradingSearchSelectionResult
export const TradingSearchSelectionResultZod = z.object({
    type: TradingSearchSelectionResultTypeZod.optional(),
    place: z.number().optional(),
});
export type TradingSearchSelectionResultType = z.TypeOf<typeof TradingSearchSelectionResultZod>;


// #/components/schemas/tradingSearchSelectionV2
export const TradingSearchSelectionV2Zod = z.object({
    selectionId: z.string(),
    name: z.string(),
    price: TradingSearchPriceZod.optional(),
    spPrice: TradingSearchPriceZod.optional(),
    selectionTypeId: z.string(),
    active: z.boolean(),
    state: TradingSearchTradingStateZod,
    result: TradingSearchSelectionResultZod.optional(),
});
export type TradingSearchSelectionV2Type = z.TypeOf<typeof TradingSearchSelectionV2Zod>;


// #/components/schemas/tradingSearchSettings
export const TradingSearchSettingsZod = z.object({
    startingPriceOnly: z.boolean(),
    startingPrice: z.boolean(),
});
export type TradingSearchSettingsType = z.TypeOf<typeof TradingSearchSettingsZod>;


// #/components/schemas/tradingSearchEachWayTerms
export const TradingSearchEachWayTermsZod = z.object({
    places: z.number(),
    reduction: z.string(),
});
export type TradingSearchEachWayTermsType = z.TypeOf<typeof TradingSearchEachWayTermsZod>;


// #/components/schemas/tradingSearchEachWay
export const TradingSearchEachWayZod = z.object({
    offered: z.boolean(),
    termsWithBet: z.boolean(),
    terms: z.array(TradingSearchEachWayTermsZod),
});
export type TradingSearchEachWayType = z.TypeOf<typeof TradingSearchEachWayZod>;


// #/components/schemas/tradingSearchMarketV2
export const TradingSearchMarketV2Zod = z.object({
    sportId: z.string().optional(),
    eventId: z.string(),
    marketId: z.string(),
    name: z.string(),
    active: z.boolean(),
    selections: z.array(TradingSearchSelectionV2Zod),
    settings: TradingSearchSettingsZod,
    eachWay: TradingSearchEachWayZod,
    line: z.number().optional(),
    marketType: z.string(),
});
export type TradingSearchMarketV2Type = z.TypeOf<typeof TradingSearchMarketV2Zod>;


// #/components/schemas/tradingSearchPagedResponseMarketV2
export const TradingSearchPagedResponseMarketV2Zod = z.object({
    items: z.array(TradingSearchMarketV2Zod),
    pagination: TradingSearchPaginationInfoZod,
});
export type TradingSearchPagedResponseMarketV2Type = z.TypeOf<typeof TradingSearchPagedResponseMarketV2Zod>;

